import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Renders one block from the static guide content registry
 * (`/app/frontend/src/lib/blogPosts.js`). The schema is intentionally
 * simple: typed objects so each guide is git-versioned and
 * crawlable without a markdown parser.
 *
 * Block schemas:
 *   { type: "p", text }
 *   { type: "h2", text }
 *   { type: "h3", text }
 *   { type: "ul", items: [string] }
 *   { type: "quote", text, author? }
 *   { type: "callout", title?, text }   — highlighted CTA / takeaway block
 */
const StaticGuideBlock = ({ block }) => {
  if (block.type === "h2") {
    return (
      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-ink mt-10 mb-4 leading-tight">
        {block.text}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 className="font-heading text-xl font-bold text-brand-ink mt-6 mb-3 leading-tight">
        {block.text}
      </h3>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="list-disc pl-6 space-y-2 my-4 text-brand-ink/85 text-base sm:text-lg leading-relaxed">
        {block.items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="border-l-4 border-brand-forest bg-brand-light-green/40 pl-5 pr-4 py-4 my-6 rounded-r-2xl italic text-brand-ink/85">
        <p className="leading-relaxed">"{block.text}"</p>
        {block.author && (
          <footer className="text-sm not-italic font-semibold text-brand-forest mt-2">
            — {block.author}
          </footer>
        )}
      </blockquote>
    );
  }
  if (block.type === "callout") {
    return (
      <aside className="my-8 bg-brand-light-green/60 border border-brand-forest/30 rounded-3xl p-6 sm:p-7">
        {block.title && (
          <div className="font-heading text-xl sm:text-2xl font-bold text-brand-ink mb-2">
            {block.title}
          </div>
        )}
        <p className="text-base sm:text-lg text-brand-ink/85 leading-relaxed">
          {block.text}
        </p>
        <Link
          to="/search"
          data-testid="guide-callout-cta"
          className="inline-flex items-center gap-2 mt-4 bg-brand-forest text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-brand-forest-dark transition-colors active:scale-95 shadow-warm"
        >
          Try Pawground free <ArrowRight className="w-4 h-4" />
        </Link>
      </aside>
    );
  }
  // default: paragraph (also fallback for unknown block types so a typo in
  // POSTS doesn't render an empty paragraph).
  if (!block.text) return null;
  return (
    <p className="text-base sm:text-lg text-brand-ink/85 leading-relaxed my-4">
      {block.text}
    </p>
  );
};

export default StaticGuideBlock;
